import { useEffect, useState } from 'react';
import type { OrderDto, OrderStatus } from '@ecom/contracts';
import { useOrdersStore } from '@/stores/orders.store';
import { useT, useStatusLabel } from '@/i18n/use-t';
import { ToggleField } from '@/components/fields';
import { Sheet } from '@/components/page';
import { Alert, Button, Field, Input, Pill, Select, Textarea, cx } from '@/components/ui';
import { nextStatuses, notifiesCustomer, requiresReason, requiresTracking, statusTone } from '@/lib/order-status';

/**
 * Changing an order's status, with what goes with it (ADR-059).
 *
 * One panel for every move, so the operator learns it once: pick where the
 * order goes from the moves it can make now, say a word about it if there is
 * one worth saying — required when it is a cancellation or a payment taken by
 * hand — and decide whether the customer hears about it. Shipping adds the
 * tracking the customer will use — the carrier picked from the store's list,
 * its tracking link built from the carrier's template (ADR-061); cancelling
 * asks whether the units go back on the shelf.
 */
export function StatusSheet({
  order,
  initial,
  onClose,
}: {
  order: OrderDto;
  /** The move the header's button proposed; the panel opens on it. Null keeps it closed. */
  initial: OrderStatus | null;
  onClose: () => void;
}) {
  const t = useT();
  const statusLabel = useStatusLabel();
  const { updating, currentError, updateStatus, carriers, loadCarriers } = useOrdersStore();

  const moves = nextStatuses(order.status, order.isPickup);
  const [target, setTarget] = useState<OrderStatus | null>(initial);
  const [note, setNote] = useState('');
  const [notify, setNotify] = useState(true);
  const [restock, setRestock] = useState(true);
  const [carrier, setCarrier] = useState('');
  const [otherCarrier, setOtherCarrier] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [trackingUrl, setTrackingUrl] = useState('');
  const [trackingUrlEdited, setTrackingUrlEdited] = useState(false);

  // Each opening starts clean, on the move it was opened for.
  useEffect(() => {
    if (initial === null) return;
    setTarget(initial);
    setNote('');
    setNotify(true);
    setRestock(true);
    // The courier the order's method names, when it names one (ADR-088).
    setCarrier(order.methodCarrier ?? '');
    setOtherCarrier(false);
    setTrackingNumber('');
    setTrackingUrl('');
    setTrackingUrlEdited(false);
  }, [initial]);

  const needsTracking = target !== null && requiresTracking(target);

  useEffect(() => {
    if (needsTracking) void loadCarriers();
  }, [needsTracking, loadCarriers]);

  // The tracking link follows the carrier and the number until someone types their own.
  const template = carriers?.find((entry) => entry.name === carrier)?.trackingUrlTemplate;
  useEffect(() => {
    if (trackingUrlEdited) return;
    setTrackingUrl(template && trackingNumber.trim() ? template.split('{number}').join(encodeURIComponent(trackingNumber.trim())) : '');
  }, [template, trackingNumber, trackingUrlEdited]);

  const needsReason = target !== null && requiresReason(target, order.status);
  const tells = target !== null && notifiesCustomer(target);
  const canSubmit =
    target !== null &&
    (!needsTracking || (carrier.trim() !== '' && trackingNumber.trim() !== '')) &&
    (!needsReason || note.trim() !== '');

  async function submit() {
    if (!target || !canSubmit) return;
    const ok = await updateStatus(order.id, {
      status: target,
      ...(note.trim() ? { reason: note.trim() } : {}),
      ...(tells ? { notifyCustomer: notify } : {}),
      ...(target === 'CANCELLED' ? { restock } : {}),
      ...(needsTracking
        ? {
            carrier: carrier.trim(),
            trackingNumber: trackingNumber.trim(),
            ...(trackingUrl.trim() ? { trackingUrl: trackingUrl.trim() } : {}),
          }
        : {}),
    });
    if (ok) onClose();
  }

  const destructive = target === 'CANCELLED';

  return (
    <Sheet
      open={initial !== null}
      title={t('changeStatusTitle')}
      subtitle={t('changeStatusBody')}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={updating}>
            {t('cancel')}
          </Button>
          <Button variant={destructive ? 'danger' : 'primary'} onClick={() => void submit()} disabled={!canSubmit || updating}>
            {updating
              ? t('saving')
              : needsTracking
                ? t('confirmShipment')
                : destructive
                  ? t('confirmCancellation')
                  : target === 'PAID' && order.status === 'PENDING_PAYMENT'
                    ? t('confirmMarkPaid')
                    : t('saveStatus')}
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-5"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-2 text-sm font-medium text-ink">{t('newStatus')}</legend>
          <div className="flex items-center gap-2 text-xs text-ink-muted">
            <Pill tone={statusTone(order.status)}>{statusLabel(order.status, order.isPickup)}</Pill>
            <span aria-hidden className="rtl:-scale-x-100">→</span>
          </div>
          {moves.map((move) => (
            <label
              key={move}
              className={cx(
                'flex cursor-pointer items-center gap-3 rounded-control border px-3 py-2.5 transition-colors',
                target === move ? 'border-brand-500 bg-surface-sunken ring-1 ring-brand-500' : 'border-line hover:bg-surface-sunken',
              )}
            >
              <input
                type="radio"
                name="order-status"
                value={move}
                checked={target === move}
                onChange={() => setTarget(move)}
                className="size-4 accent-brand-600"
              />
              <span className={cx('text-sm', move === 'CANCELLED' ? 'text-danger' : 'text-ink')}>{statusLabel(move)}</span>
            </label>
          ))}
        </fieldset>

        {needsTracking ? (
          <div className="flex flex-col gap-4 rounded-control border border-line p-4">
            <p className="text-xs text-ink-muted">{t('shipDialogBody')}</p>
            {/* The store's carriers (Settings → Shipping), with a way out for a one-off. */}
            <Field label={t('carrier')} htmlFor="status-carrier">
              {carriers && carriers.length > 0 && !otherCarrier ? (
                <Select
                  id="status-carrier"
                  className="w-full"
                  value={carrier}
                  onChange={(event) => {
                    if (event.target.value === '__other') {
                      setOtherCarrier(true);
                      setCarrier('');
                    } else setCarrier(event.target.value);
                  }}
                >
                  <option value="">{t('carrierChoose')}</option>
                  {carriers.map((entry) => (
                    <option key={entry.name} value={entry.name}>
                      {entry.name}
                    </option>
                  ))}
                  <option value="__other">{t('carrierOther')}</option>
                </Select>
              ) : (
                <Input
                  id="status-carrier"
                  value={carrier}
                  placeholder={t('carrierName')}
                  onChange={(event) => setCarrier(event.target.value)}
                />
              )}
            </Field>
            <Field label={t('trackingNumber')} htmlFor="status-tracking">
              <Input
                id="status-tracking"
                dir="ltr"
                value={trackingNumber}
                onChange={(event) => setTrackingNumber(event.target.value)}
              />
            </Field>
            <Field label={t('trackingUrl')} htmlFor="status-tracking-url" hint={t('optional')}>
              <Input
                id="status-tracking-url"
                dir="ltr"
                inputMode="url"
                value={trackingUrl}
                onChange={(event) => {
                  setTrackingUrlEdited(true);
                  setTrackingUrl(event.target.value);
                }}
              />
            </Field>
          </div>
        ) : null}

        {target === 'PAID' && order.status === 'PENDING_PAYMENT' ? (
          <Alert tone="info">{t('markPaidDialogBody')}</Alert>
        ) : null}

        {target === 'DELIVERED' && order.paymentMethod === 'COD' && order.payment?.status === 'PENDING' ? (
          // Delivery is when the cash changes hands; marking it records the money (ADR-061).
          <Alert tone="info">{t('codCollectsOnDelivery')}</Alert>
        ) : null}

        {target === 'CANCELLED' ? <p className="text-xs text-ink-muted">{t('cancelOrderHint')}</p> : null}

        <Field
          label={t('statusNote')}
          htmlFor="status-note"
          hint={needsReason ? t('statusNoteRequiredHint') : t('statusNoteHint')}
        >
          <Textarea
            id="status-note"
            rows={3}
            maxLength={500}
            value={note}
            invalid={needsReason && note.trim() === '' && note !== ''}
            onChange={(event) => setNote(event.target.value)}
          />
        </Field>

        {target === 'CANCELLED' ? (
          <ToggleField
            label={t('restockItems')}
            hint={t('restockItemsHint')}
            checked={restock}
            onChange={setRestock}
          />
        ) : null}

        {target !== null ? (
          tells ? (
            <ToggleField label={t('notifyCustomer')} hint={t('notifyCustomerHint')} checked={notify} onChange={setNotify} />
          ) : (
            <p className="text-xs text-ink-muted">{t('noCustomerMessage')}</p>
          )
        ) : null}

        {currentError ? <Alert>{currentError}</Alert> : null}
      </form>
    </Sheet>
  );
}
